import React from "react"
import { Accordion, As, Box, Heading, Text } from "@chakra-ui/react"

import { withSection } from "@app/hoc/Section"
import { useCore } from "@app/hooks/useCore"
import AccordionGroupItem from "@app/components/Sections/AccordionGroup/AccordionGroupItem"
import AccordionGroupGrossStuffItem from "@app/components/Sections/AccordionGroup/AccordionGroupGrossStuffItem"
import AccordionGroupItemBase from "@app/components/Sections/AccordionGroup/AccordionGroupItemBase"
import AccordionGroupKeyIngredientsItem from "@app/components/Sections/AccordionGroup/AccordionGroupKeyIngredientsItem"

type Props = {
  innerRef: any
  inView: boolean
  accordions: GatsbyTypes.SanitySectionAccordionGroup["accordions"]
  style: GatsbyTypes.SanitySectionAccordionGroup["style"]
  handleTrackingClick: () => void
  grossStuff: GatsbyTypes.TemplateFlexibleQuery["grossStuff"]
  tag: As
}

const AccordionGroup: React.FC<Props> = ({ innerRef, accordions, style, grossStuff, tag }) => {
  const {
    helpers: { handleize },
  } = useCore()

  const isProduct = style === "product"
  const isSkinSupport = style === "skin-support"
  const isGiftcard = style === "giftcard"

  return (
    <Box as="section" ref={innerRef} color="brand.black">
      <Box padding={isProduct ? 0 : { base: isGiftcard ? "0" : "20px", sm: "30px" }} pt={isProduct ? 0 : { sm: "60px" }}>
        <Box maxW="1200px" mx="auto" mt={isProduct ? 0 : "40px"} px={isProduct ? "20px" : 0}>
          {accordions?.map((accordion, index) => (
            <Box key={index} mb={isProduct ? 0 : "40px"} pb={isProduct ? 0 : { base: "40px", sm: "60px" }} bgColor="primary.white">
              <Heading
                as={!index ? tag || "h2" : "h2"}
                id={accordion?.title ? handleize(accordion?.title) : ""}
                fontSize={{ base: "26px", md: "46px" }}
                lineHeight={1.3}
                textAlign={isSkinSupport || isGiftcard ? "center" : "inherit"}
                {...(isGiftcard && {
                  fontFamily: "Larsseit",
                  fontSize: { base: "24px", md: "48px" },
                  fontWeight: 700,
                })}
              >
                {accordion?.title}
              </Heading>
              {accordion?.subtitle && (
                <Text
                  variant="paragraph16"
                  lineHeight={{ base: 1.4, sm: 1.5 }}
                  mb="20px"
                  maxW="600px"
                  marginInline={isSkinSupport ? "auto" : 0}
                  textAlign={isSkinSupport ? "center" : "inherit"}
                >
                  {accordion?.subtitle}
                </Text>
              )}
              <Accordion
                allowToggle
                allowMultiple={isProduct}
                defaultIndex={accordion?.accordionItems?.[0]?._type === "accordionItem" ? [0] : []}
                mt={isProduct || isSkinSupport ? 0 : "30px"}
                borderColor="brand.black"
              >
                {accordion?.accordionItems?.map((accordionItem, index) => (
                  <AccordionGroupItemBase
                    key={index}
                    title={accordionItem?._type === "accordionItem" ? accordionItem?.question : accordionItem?.title}
                    style={style}
                    hasBottomBorder={!isProduct}
                    hasTopBorder={!isSkinSupport}
                  >
                    {accordionItem?._type === "accordionItem" ? (
                      <AccordionGroupItem key={index} item={accordionItem} style={style} />
                    ) : accordionItem?._type === "grossStuffAccordionItem" ? (
                      <AccordionGroupGrossStuffItem key={index} grossStuff={grossStuff} />
                    ) : accordionItem?._type === "keyIngredientsAccordionItem" ? (
                      <AccordionGroupKeyIngredientsItem key={index} item={accordionItem} />
                    ) : null}
                  </AccordionGroupItemBase>
                ))}
              </Accordion>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default React.memo(withSection(AccordionGroup))
